import { adminDayStartOf } from "../format/admin-time";

export const PAGE_SIZE = 50;

export interface AuditLogRow {
  id: string;
  action: string;
  actorEmail: string;
  targetType: string | null;
  targetId: string | null;
  metadata: unknown;
  createdAt: Date;
}

export interface AuditFilters {
  actor: string | null;
  action: string | null;
  from: Date | null;
  to: Date | null;
  page: number;
}

export interface AuditRepoWhere {
  actorEmail?: string;
  action?: string;
  createdAtGte?: Date;
  createdAtLte?: Date;
}

export interface AuditLogRepo {
  count(where: AuditRepoWhere): Promise<number>;
  findRange(
    where: AuditRepoWhere,
    skip: number,
    take: number,
  ): Promise<AuditLogRow[]>;
}

export interface AuditLogPage {
  rows: AuditLogRow[];
  total: number;
  hasMore: boolean;
  page: number;
}

function nextDay(ymd: string): string {
  const d = new Date(`${ymd}T00:00:00.000Z`);
  return new Date(d.getTime() + 86_400_000).toISOString().slice(0, 10);
}

// A bare YYYY-MM-DD is a whole day on the admins' (IST) clock: "from"
// is its first instant, "to" its last. Audit rows are moments.
function parseDate(raw: string, field: "from" | "to"): Date {
  const t = raw.trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(t)) {
    const start = adminDayStartOf(t);
    if (Number.isNaN(start.getTime())) throw new Error(`Invalid ${field} date: ${raw}`);
    return field === "to"
      ? new Date(adminDayStartOf(nextDay(t)).getTime() - 1)
      : start;
  }
  const d = new Date(t);
  if (Number.isNaN(d.getTime())) {
    throw new Error(`Invalid ${field} date: ${raw}`);
  }
  return d;
}

export function parseAuditFilters(params: URLSearchParams): AuditFilters {
  const rawActor = params.get("actor")?.trim();
  const rawAction = params.get("action")?.trim();
  const rawFrom = params.get("from");
  const rawTo = params.get("to");
  const rawPage = params.get("page");

  const pageInt = rawPage ? Number.parseInt(rawPage, 10) : 1;
  const page = Number.isFinite(pageInt) && pageInt > 0 ? pageInt : 1;

  return {
    actor: rawActor ? rawActor.toLowerCase() : null,
    action: rawAction ? rawAction : null,
    from: rawFrom ? parseDate(rawFrom, "from") : null,
    to: rawTo ? parseDate(rawTo, "to") : null,
    page,
  };
}

function toWhere(filters: AuditFilters): AuditRepoWhere {
  const where: AuditRepoWhere = {};
  if (filters.actor) where.actorEmail = filters.actor;
  if (filters.action) where.action = filters.action;
  if (filters.from) where.createdAtGte = filters.from;
  if (filters.to) where.createdAtLte = filters.to;
  return where;
}

export async function listAuditLog(
  repo: AuditLogRepo,
  filters: AuditFilters,
): Promise<AuditLogPage> {
  const where = toWhere(filters);
  const skip = (filters.page - 1) * PAGE_SIZE;
  const [total, rows] = await Promise.all([
    repo.count(where),
    repo.findRange(where, skip, PAGE_SIZE),
  ]);
  return {
    rows,
    total,
    hasMore: skip + rows.length < total,
    page: filters.page,
  };
}
